import {
  advanceDiagnosticReportsCursor,
  usePatientDiagnosticReports,
  usePatientDiagnosticReportsPage,
} from '@ctw/shared/api/fhir/diagnostic-report';
import type { DiagnosticReportModel } from '@ctw/shared/api/fhir/models/diagnostic-report';
import { withErrorBoundary } from '@ctw/shared/components/errors/error-boundary';
import { useMainContentHeader } from '@ctw/shared/components/layout/main-content-with-dockable-drawer';
import { Pagination } from '@ctw/shared/components/pagination/pagination';
import { SimplePagination } from '@ctw/shared/components/pagination/simple-pagination';
import { useDiagnosticRowActions } from '@ctw/shared/content/diagnostic-reports/helpers/row-actions';
import { EmptyPatientTable } from '@ctw/shared/content/empty-patients-table';
import { ResourceTable } from '@ctw/shared/content/resource/resource-table';
import type { ResourceTableSortOption } from '@ctw/shared/content/resource/resource-table-actions';
import { useCTW } from '@ctw/shared/context/ctw-context';
import { useFeatureFlag } from '@ctw/shared/context/feature-flag-provider';
import { usePatientContext } from '@ctw/shared/context/patient-provider';
import { useResourceDrawer } from '@ctw/shared/context/resource-drawer-context';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { useWritebacks } from '@ctw/shared/context/writeback-provider';
import { useFilteredSortedData } from '@ctw/shared/hooks/use-filtered-sorted-data';
import { getDefaultsFromLocalStorage, saveToLocalStorage } from '@ctw/shared/utils/local-storage';
import { useEffect, useState } from 'react';
import { ResourceTableActions } from '../resource/resource-table-actions';
import { patientDiagnosticReportsColumns } from './helpers/columns';
import {
  defaultDiagnosticReportSort,
  diagnosticReportSortOptions,
  diagnosticReportSortToFqsSortParams,
} from './helpers/sorts';

const PatientDiagnosticReportsClientSide = () => {
  const { requestContext } = useCTW();
  const rowActions = useDiagnosticRowActions();
  const { openResourceDrawer } = useResourceDrawer({ resourceActions: rowActions as never });
  const query = usePatientDiagnosticReports();
  const { writebackStatuses } = useWritebacks();

  const { data, setSort, defaultSort } = useFilteredSortedData({
    cacheKey: 'patient-diagnostic-reports',
    defaultSort: defaultDiagnosticReportSort,
    records: query.data,
  });
  const isEmptyQuery = !query.data || query.data.length === 0;
  const hasZeroFilteredRecords = !isEmptyQuery && data.length === 0;

  useMainContentHeader(
    'PatientDiagnosticReportsContentHeader',
    () => (
      <ResourceTableActions
        sortOptions={{
          defaultSort,
          options: diagnosticReportSortOptions,
          onChange: setSort,
        }}
      />
    ),
    [diagnosticReportSortOptions, defaultSort],
  );

  return (
    <div>
      <ResourceTable
        title="Diagnostic Reports"
        titleVisuallyHidden={true}
        showTableHead={true}
        isLoading={query.isLoading}
        data={data}
        stackedBreakpoint="xs"
        columns={patientDiagnosticReportsColumns(requestContext.builderId, writebackStatuses)}
        onRowClick={({ telemetry, record }) =>
          openResourceDrawer({
            telemetry,
            model: record,
          })
        }
        rowActions={rowActions}
        enableReadActions={true}
        emptyMessage={
          <EmptyPatientTable
            hasZeroFilteredRecords={hasZeroFilteredRecords}
            resourceName="diagnostics"
          />
        }
      />
    </div>
  );
};

const DIAGNOSTIC_REPORTS_PAGE_SIZE = 25;

// Resolves the persisted sort selection to a canonical option so identity checks and the sort
// menu stay consistent (localStorage strips functions/predicates from the cached value).
const resolveInitialSort = (): ResourceTableSortOption<DiagnosticReportModel> => {
  const { defaultSort } = getDefaultsFromLocalStorage('patient-diagnostic-reports', {
    defaultSort: defaultDiagnosticReportSort,
  });
  return (
    diagnosticReportSortOptions.find((option) => option.display === defaultSort.display) ??
    defaultDiagnosticReportSort
  );
};

const PatientDiagnosticReportsServerSide = () => {
  const { requestContext, graphqlClient } = useCTW();
  const telemetry = useTelemetry();
  const { patient } = usePatientContext();
  const rowActions = useDiagnosticRowActions();
  const { openResourceDrawer } = useResourceDrawer({ resourceActions: rowActions as never });
  const { writebackStatuses } = useWritebacks();

  const [currentSort, setCurrentSort] =
    useState<ResourceTableSortOption<DiagnosticReportModel>>(resolveInitialSort);
  const [currentPage, setCurrentPage] = useState(1);
  // cursorsByPageIndex[k] is the cursor that fetches page k + 1; page 1 uses the empty cursor.
  const [cursorsByPageIndex, setCursorsByPageIndex] = useState<Record<number, string>>({ 0: '' });
  const [isJumping, setIsJumping] = useState(false);
  // Hold the last non-null total so the pager doesn't flicker between page transitions.
  const [lastKnownTotal, setLastKnownTotal] = useState<number | null>(null);

  const query = usePatientDiagnosticReportsPage(
    DIAGNOSTIC_REPORTS_PAGE_SIZE,
    cursorsByPageIndex[currentPage - 1] ?? '',
    diagnosticReportSortToFqsSortParams(currentSort),
  );

  // Store the fetched page's endCursor so Prev/Next can revisit adjacent pages in one request.
  useEffect(() => {
    const endCursor = query.data?.pageInfo.endCursor;
    if (endCursor === undefined) {
      return;
    }
    setCursorsByPageIndex((prev) =>
      prev[currentPage] === endCursor ? prev : { ...prev, [currentPage]: endCursor },
    );
  }, [query.data, currentPage]);

  useEffect(() => {
    const total = query.data?.pageInfo.total;
    if (typeof total === 'number') {
      setLastKnownTotal(total);
    }
  }, [query.data]);

  const handleSortChange = (option: ResourceTableSortOption<DiagnosticReportModel>) => {
    setCurrentSort(option);
    // Persist under the same cache key as the flag-OFF path so the preference survives flag flips.
    saveToLocalStorage('patient-diagnostic-reports', { defaultSort: option });
    // FQS rejects a cursor minted under a different sort, so reset pagination on every change.
    setCursorsByPageIndex({ 0: '' });
    setCurrentPage(1);
  };

  const handlePageChange = (target: number) => {
    if (target === currentPage) {
      return;
    }
    if (cursorsByPageIndex[target - 1] !== undefined) {
      // Prev/Next and any previously visited page: fetch directly from the known cursor.
      setCurrentPage(target);
      return;
    }

    // Far jump to an unseen page: advance from the nearest known cursor before the target.
    const knownIndexes = Object.keys(cursorsByPageIndex)
      .map(Number)
      .filter((index) => index < target - 1);
    const fromPageIndex = Math.max(0, ...knownIndexes);
    const count = (target - 1 - fromPageIndex) * DIAGNOSTIC_REPORTS_PAGE_SIZE;

    setIsJumping(true);
    advanceDiagnosticReportsCursor(
      telemetry,
      graphqlClient,
      patient,
      {
        cursor: cursorsByPageIndex[fromPageIndex] ?? '',
        fromPageIndex,
        pageSize: DIAGNOSTIC_REPORTS_PAGE_SIZE,
        count,
        sort: diagnosticReportSortToFqsSortParams(currentSort),
      },
      { requestHeaders: { 'X-Use-Hotstorage': '1' } },
    )
      .then(({ cursorsByPageIndex: discovered, exhausted }) => {
        // Functional update: a page visited while this jump was in flight (e.g. Prev/Next to an
        // already-known page) can have added its own endCursor since the jump started. Merging
        // into the latest state, rather than the state captured when the jump began, keeps that
        // entry instead of overwriting it with a stale pre-jump value.
        setCursorsByPageIndex((prev) => ({ ...prev, ...discovered }));
        // If the connection ran out mid-jump, clamp to the last page this jump actually reached.
        // Basing that on `discovered` — this skip's own page-aligned boundaries — means a
        // connection that shrank since an earlier page was visited can't resurrect a now-stale
        // cursor beyond where this skip ran out. Falling back further to `fromPageIndex + 1`
        // covers the jump exhausting before confirming even one full page — that's the page the
        // jump started from, already known reachable before this call.
        const reachedIndexes = Object.keys(discovered).map(Number);
        const furthestReachedPage =
          reachedIndexes.length > 0 ? Math.max(...reachedIndexes) + 1 : fromPageIndex + 1;
        const reachable = exhausted ? furthestReachedPage : target;
        setCurrentPage(reachable);
      })
      .catch((error) => {
        // Leave the current page unchanged on failure; the table stops its jump spinner below.
        telemetry.logger.error('Failed to advance diagnostic reports cursor', { error });
      })
      .finally(() => {
        setIsJumping(false);
      });
  };

  useMainContentHeader(
    'PatientDiagnosticReportsContentHeader',
    () => (
      <ResourceTableActions
        sortOptions={{
          defaultSort: currentSort,
          options: diagnosticReportSortOptions,
          onChange: handleSortChange,
        }}
      />
    ),
    [currentSort],
  );

  const total = lastKnownTotal;

  return (
    <div>
      <ResourceTable
        title="Diagnostic Reports"
        titleVisuallyHidden={true}
        showTableHead={true}
        isLoading={query.isLoading || isJumping}
        data={query.data?.reports ?? []}
        stackedBreakpoint="xs"
        columns={patientDiagnosticReportsColumns(requestContext.builderId, writebackStatuses)}
        onRowClick={({ telemetry: rowTelemetry, record }) =>
          openResourceDrawer({
            telemetry: rowTelemetry,
            model: record,
          })
        }
        rowActions={rowActions}
        enableReadActions={true}
        hidePagination={true}
        emptyMessage={
          <EmptyPatientTable hasZeroFilteredRecords={false} resourceName="diagnostics" />
        }
      />
      {typeof total === 'number' ? (
        <Pagination
          currentPage={currentPage}
          setCurrentPage={handlePageChange}
          pageSize={DIAGNOSTIC_REPORTS_PAGE_SIZE}
          total={total}
        />
      ) : (
        <SimplePagination
          currentPage={currentPage}
          setCurrentPage={handlePageChange}
          hasNext={Boolean(query.data?.pageInfo.hasNextPage)}
        />
      )}
    </div>
  );
};

export const PatientDiagnosticReports = withErrorBoundary({
  boundaryName: 'PatientDiagnosticReports',
  Component: () => {
    const serverSideViewControls = useFeatureFlag(
      'ctw-diagnostics-resource-view-controls-server-side',
    );
    return serverSideViewControls ? (
      <PatientDiagnosticReportsServerSide />
    ) : (
      <PatientDiagnosticReportsClientSide />
    );
  },
});
